"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CertificateSheet } from "@/components/tools/printables/certificate-page";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ACCENTS,
  type AccentKey,
  type BorderStyle,
  type CertificateFont,
  type CertificateText,
  fontSet,
  layoutCertificate,
  parseRecipients,
} from "@/lib/tool-kit/printables/certificate";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const CERTIFICATE_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  templateLabel: "Start from a template",
  templates: {
    achievement: {
      label: "Achievement",
      title: "Certificate of Achievement",
      intro: "This certificate is proudly presented to",
      body: "in recognition of outstanding effort and achievement",
    },
    completion: {
      label: "Completion",
      title: "Certificate of Completion",
      intro: "This certifies that",
      body: "has successfully completed the course",
    },
    participation: {
      label: "Participation",
      title: "Certificate of Participation",
      intro: "This certificate is awarded to",
      body: "for taking part and giving their best",
    },
    excellence: {
      label: "Excellence",
      title: "Certificate of Excellence",
      intro: "Presented with pride to",
      body: "for excellence and dedication",
    },
    appreciation: {
      label: "Appreciation",
      title: "Certificate of Appreciation",
      intro: "With sincere thanks to",
      body: "for generous time, help and support",
    },
  },
  organisationLabel: "School or organisation (optional)",
  organisationPlaceholder: "Riverside Elementary School",
  titleLabel: "Certificate title",
  introLabel: "Line above the name",
  recipientsLabel: "Recipient names — one per line, one certificate each",
  recipientsHint:
    "Leave empty to print one certificate with a blank line to write the name on by hand.",
  bodyLabel: "Reason or details",
  dateLabelLabel: "Date line label",
  dateTextLabel: "Date (optional)",
  dateTextHint:
    "Type the date as you want it to appear, or leave it blank to write it by hand.",
  signatureLabelLabel: "Signature line label",
  dateLabel: "Date",
  signatureLabel: "Signature",
  borderLabel: "Border",
  borders: {
    none: "No border",
    simple: "Simple line",
    double: "Double line",
    classic: "Classic with corners",
    stars: "Stars",
  },
  accentLabel: "Colour",
  accents: {
    navy: "Navy",
    burgundy: "Burgundy",
    forest: "Forest green",
    gold: "Antique gold",
    purple: "Purple",
    black: "Black and white",
  },
  fontLabel: "Lettering",
  fonts: {
    classic: "Classic — serif",
    modern: "Modern — sans-serif",
    script: "Classic with a script name",
  },
  fontHint:
    "Fonts come from your device, and the preview is what prints. The script name uses your device's script font.",
  sealLabel: "Add a seal",
  previewLabel: "Certificate preview",
} as const;

type TemplateKey = keyof typeof CERTIFICATE_GENERATOR_STRINGS.templates;

interface Form {
  template: TemplateKey;
  organisation: string;
  title: string;
  intro: string;
  recipients: string;
  body: string;
  dateText: string;
  dateLabel: string;
  signatureLabel: string;
  orientation: Orientation;
  paper: PaperSize;
  border: BorderStyle;
  accent: AccentKey;
  font: CertificateFont;
  seal: boolean;
}

export default function CertificateGeneratorWidget() {
  const strings = useToolUi(CERTIFICATE_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const start = strings.templates.achievement;
  const [form, setForm] = useState<Form>({
    template: "achievement",
    organisation: "",
    title: start.title,
    intro: start.intro,
    recipients: "",
    body: start.body,
    dateText: "",
    dateLabel: strings.dateLabel,
    signatureLabel: strings.signatureLabel,
    orientation: "landscape",
    paper: "letter",
    border: "classic",
    accent: "navy",
    font: "classic",
    seal: true,
  });
  const update = (patch: Partial<Form>) =>
    setForm((previous) => ({ ...previous, ...patch }));

  function chooseTemplate(template: TemplateKey) {
    const chosen = strings.templates[template];
    update({
      template,
      title: chosen.title,
      intro: chosen.intro,
      body: chosen.body,
    });
  }

  const geometry = pageGeometry(form.paper, form.orientation);
  const fonts = fontSet(form.font);
  const text: CertificateText = {
    organisation: form.organisation,
    title: form.title,
    intro: form.intro,
    body: form.body,
    dateLabel: form.dateLabel,
    dateText: form.dateText,
    signatureLabel: form.signatureLabel,
  };
  const layout = layoutCertificate(geometry, measure, text, fonts, form.seal);
  const names = parseRecipients(form.recipients);
  const recipients = names.length > 0 ? names : [""];
  const pages = recipients.map((name, index) => (
    <CertificateSheet
      accent={ACCENTS[form.accent]}
      border={form.border}
      fonts={fonts}
      key={`${index}-${name}`}
      layout={layout}
      measure={measure}
      name={name}
    />
  ));

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <SelectField
          id="cg-template"
          label={strings.templateLabel}
          onChange={chooseTemplate}
          options={(Object.keys(strings.templates) as TemplateKey[]).map(
            (value) => ({ value, label: strings.templates[value].label })
          )}
          value={form.template}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="cg-title"
            label={strings.titleLabel}
            onChange={(title) => update({ title })}
            value={form.title}
          />
          <TextField
            id="cg-intro"
            label={strings.introLabel}
            onChange={(intro) => update({ intro })}
            value={form.intro}
          />
          <TextField
            id="cg-org"
            label={strings.organisationLabel}
            onChange={(organisation) => update({ organisation })}
            placeholder={strings.organisationPlaceholder}
            value={form.organisation}
          />
          <TextField
            hint={strings.dateTextHint}
            id="cg-date"
            label={strings.dateTextLabel}
            onChange={(dateText) => update({ dateText })}
            value={form.dateText}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={strings.recipientsHint}
            id="cg-names"
            label={strings.recipientsLabel}
            onChange={(recipients) => update({ recipients })}
            prose
            rows={5}
            value={form.recipients}
          />
          <TextAreaField
            id="cg-body"
            label={strings.bodyLabel}
            onChange={(body) => update({ body })}
            prose
            rows={5}
            value={form.body}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            id="cg-date-label"
            label={strings.dateLabelLabel}
            onChange={(dateLabel) => update({ dateLabel })}
            value={form.dateLabel}
          />
          <TextField
            id="cg-sign-label"
            label={strings.signatureLabelLabel}
            onChange={(signatureLabel) => update({ signatureLabel })}
            value={form.signatureLabel}
          />
          <SelectField
            id="cg-border"
            label={strings.borderLabel}
            onChange={(border) => update({ border })}
            options={(Object.keys(strings.borders) as BorderStyle[]).map(
              (value) => ({ value, label: strings.borders[value] })
            )}
            value={form.border}
          />
          <SelectField
            id="cg-accent"
            label={strings.accentLabel}
            onChange={(accent) => update({ accent })}
            options={(Object.keys(ACCENTS) as AccentKey[]).map((value) => ({
              value,
              label: strings.accents[value],
            }))}
            value={form.accent}
          />
          <SelectField
            hint={strings.fontHint}
            id="cg-font"
            label={strings.fontLabel}
            onChange={(font) => update({ font })}
            options={(["classic", "modern", "script"] as const).map(
              (value) => ({ value, label: strings.fonts[value] })
            )}
            value={form.font}
          />
          <PaperControls
            idPrefix="cg"
            onOrientation={(orientation) => update({ orientation })}
            onPaper={(paper) => update({ paper })}
            orientation={form.orientation}
            paper={form.paper}
            strings={strings}
          />
        </div>
        <Checkbox
          checked={form.seal}
          label={strings.sealLabel}
          onChange={(seal) => update({ seal })}
        />
        <PrintBar
          fileName={`certificate-${form.paper}`}
          pageTotal={pages.length}
          paper={form.paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={form.orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
